<template>
  <div>
    <div v-if="runs.length > 0" class="ibox float-e-margins mass-run-stack">
      <div v-for="runData in runs" :key="runData.run.id"
           :class="{'is-new-run': justStartedRunId === runData.run.id}"
           class="mass-run-card">
        <div class="mass-run-header" @click="$emit('toggle-run', runData.run.id)">
          <div class="mass-run-title">
            <span class="mass-run-toggle">
              <i :class="openRuns[runData.run.id] ? 'fa-chevron-down' : 'fa-chevron-right'" class="fa"></i>
            </span>
            <span :class="{'is-live': runData.run.status === 'running'}" class="mass-run-icon">
              <i class="fa fa-tasks"></i>
            </span>
            <span>
              <span class="mass-run-name">{{ labels.massUpdate }}</span>
              <span class="mass-run-date">{{ runData.run.started_at ? formatDate(runData.run.started_at) : '-' }}</span>
            </span>
          </div>
          <span :class="getRunStatusClass(runData.run)" class="label mass-run-status">
            {{ formatRunStatus(runData.run) }}
          </span>
        </div>
        <div v-show="openRuns[runData.run.id]" class="ibox-content mass-run-body">
          <div class="mass-run-meta-grid">
            <div class="mass-run-meta">
              <small>Gestartet von</small>
              <div class="font-bold text-ellipsis">{{
                  runData.run.started_by_name ? runData.run.started_by_name : (runData.run.started_by ? 'ID #' + runData.run.started_by : '-')
                }}
              </div>
            </div>
            <div class="mass-run-meta">
              <small>Gestartet am</small>
              <div class="font-bold small">{{
                  runData.run.started_at ? formatDate(runData.run.started_at) : '-'
                }}
              </div>
            </div>
          </div>

          <div class="mass-run-config">
            <div class="mass-section-title">Konfiguration</div>
            <div class="mass-chip-row">
              <span v-if="runData.run.platforms && runData.run.platforms.includes('ios')"
                    class="mass-chip mass-chip-ios"><i class="fa fa-apple"></i> iOS</span>
              <span v-if="runData.run.platforms && runData.run.platforms.includes('android')"
                    class="mass-chip mass-chip-android"><i class="fa fa-android"></i> Android</span>
              <span v-if="runData.run.include_publishing" class="mass-chip mass-chip-publishing">
                <i class="fa fa-cloud-upload"></i> Publishing
              </span>
              <span v-if="shouldShowPublishingType(runData.run, 'ios')" class="mass-chip mass-chip-ios-soft">
                <i class="fa fa-apple"></i> {{ getPublishingTypeLabel('ios', runData.run.ios_publishing_type) }}
              </span>
              <span v-if="shouldShowPublishingType(runData.run, 'android')" class="mass-chip mass-chip-android-soft">
                <i class="fa fa-android"></i> {{
                  getPublishingTypeLabel('android', runData.run.android_publishing_type)
                }}
              </span>
            </div>

            <div class="mass-run-detail-grid">
              <div v-if="getRunRepositoryName(runData.run)" :title="getRunRepositoryName(runData.run)"
                   class="mass-run-detail mass-run-detail-wide">
                <i class="fa fa-git-square"></i>
                <span><strong>Repo</strong>{{ getRunRepositoryName(runData.run) }}</span>
              </div>
              <div v-if="getRunBranchName(runData.run)" class="mass-run-detail mass-run-detail-wide">
                <i class="fa fa-code-fork"></i>
                <span><strong>Branch</strong>{{ getRunBranchName(runData.run) }}</span>
              </div>
              <div v-if="getRunNodeVersionName(runData.run)" class="mass-run-detail">
                <i class="fa fa-code"></i>
                <span><strong>Node</strong>{{ getRunNodeVersionName(runData.run) }}</span>
              </div>
              <div v-if="runData.run.java_version" class="mass-run-detail">
                <i class="fa fa-coffee"></i>
                <span><strong>Java</strong>{{ runData.run.java_version }}</span>
              </div>
              <div v-if="runData.run.ios_version" class="mass-run-detail">
                <i class="fa fa-apple"></i>
                <span><strong>iOS Version</strong>{{ runData.run.ios_version }}</span>
              </div>
              <div v-if="runData.run.android_version" class="mass-run-detail">
                <i class="fa fa-android"></i>
                <span><strong>Android Version</strong>{{ runData.run.android_version }}</span>
              </div>
            </div>

            <div v-if="runData.run.whats_new" class="mass-run-notes">
              <i class="fa fa-list-alt"></i>
              <div>
                <strong>Neues in dieser Version</strong>
                <span>{{ runData.run.whats_new }}</span>
              </div>
            </div>
          </div>

          <div class="mass-run-progress-card">
            <div class="mass-run-progress-header">
              <div>
                <small>Fortschritt</small>
                <strong>{{ runData.run.completed_builds || 0 }} / {{ runData.run.total_builds || 0 }} Apps</strong>
              </div>
              <div class="mass-run-progress-percent">
                {{ getRunProgress(runData) }}%
              </div>
            </div>
            <div class="progress progress-mini mass-run-progress">
              <div
                  :class="['progress-bar', runData.run.status === 'stopped' ? 'progress-bar-warning' : 'progress-bar-navy']"
                  :style="{width: getRunProgress(runData) + '%'}"></div>
            </div>

            <mass-run-queue
                :builds="runData.builds"
                :configurations="configurations"
                :run="runData.run"/>
          </div>

          <div v-if="runData.run.status === 'running'" class="m-t-md">
            <button class="btn btn-outline btn-danger btn-block btn-sm"
                    @click="$emit('stop-current-run', runData.run.id)">
              <i class="fa fa-stop m-r-xs"></i> Massenupdate stoppen
            </button>
          </div>
          <div v-else-if="runData.run.status === 'finished'" class="m-t-md text-center">
            <span class="text-success small font-bold"><i
                class="fa fa-check-circle m-r-xs"></i> Abgeschlossen</span>
          </div>
          <div v-else-if="runData.run.status === 'stopped'" class="m-t-md text-center">
            <span class="text-warning small font-bold"><i class="fa fa-hand-stop-o m-r-xs"></i> Gestoppt</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="runs.length === 0" class="text-center m-t-sm">
      <div class="well well-sm">
        <i class="fa fa-info-circle"></i> {{ labels.noBuild }}
      </div>
    </div>
  </div>
</template>

<script>
import {isCanceled, isFailed, isFinished, progressValue, stateValue,} from './buildStates';
import MassRunQueue from './MassRunQueue.vue';

export default {
  components: {
    MassRunQueue,
  },
  props: {
    labels: {
      type: Object,
      required: true,
    },
    runs: {
      type: Array,
      required: true,
    },
    openRuns: {
      type: Object,
      required: true,
    },
    justStartedRunId: {
      required: true,
    },
    configurations: {
      type: Array,
      required: true,
    },
    repositories: {
      type: Array,
      required: true,
    },
    branches: {
      type: Array,
      required: true,
    },
    nodeVersions: {
      type: Array,
      required: true,
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    formatRunStatus(run) {
      if (!run) return 'NICHT AKTIV';
      if (run.status === 'running') return 'WIRD AUSGEFÜHRT';
      if (run.status === 'finished') return 'ABGESCHLOSSEN';
      if (run.status === 'stopped') return 'GESTOPPT';
      if (run.status === 'failed') return 'FEHLGESCHLAGEN';
      return run.status ? run.status.toUpperCase() : 'NICHT AKTIV';
    },
    getRunStatusClass(run) {
      if (!run) return 'text-muted';
      if (run.status === 'running') return 'text-success font-bold';
      if (run.status === 'finished') return 'text-success font-bold';
      if (run.status === 'stopped') return 'text-warning font-bold';
      if (run.status === 'failed') return 'text-danger font-bold';
      return 'text-muted';
    },
    shouldShowPublishingType(run, platform) {
      return !!run
          && !!run.include_publishing
          && Array.isArray(run.platforms)
          && run.platforms.includes(platform);
    },
    getPublishingTypeLabel(platform, value) {
      const labels = {
        ios: {
          test_flight: 'TestFlight',
          app_store: 'App Store',
        },
        android: {
          internal: 'Internal Track',
          play_store: 'Play Store',
        },
      };

      return (labels[platform] && labels[platform][value]) || this.labels.defaultOption;
    },
    getRunProgress(runData) {
      const run = runData && runData.run ? runData.run : runData;
      const builds = runData && runData.builds ? runData.builds : (run && run.builds ? run.builds : null);

      if (run && (run.status === 'finished' || run.status === 'stopped' || run.status === 'failed')) {
        if (run.total_builds > 0) {
          return Math.round(((run.completed_builds || run.total_builds) / run.total_builds) * 100);
        }
        return 100;
      }

      if (!builds || builds.length === 0) return 0;

      const total = builds.length;
      const sum = builds.reduce((acc, b) => {
        const state = stateValue(b.state);
        if (isFinished(state) || isCanceled(state) || isFailed(state)) return acc + 100;
        if (state === 0) return acc + 0;
        return acc + progressValue(b);
      }, 0);

      return Math.round(sum / total);
    },
    getNodeVersion(id) {
      const version = this.nodeVersions.find(nv => nv.id === id);
      return version ? version.version : id;
    },
    getRunRepositoryName(run) {
      if (run.source_repository_name) return run.source_repository_name;
      if (run.source_repository_id) {
        const repo = this.repositories.find(r => r.id == run.source_repository_id);
        return repo ? repo.name : null;
      }
      return null;
    },
    getRunBranchName(run) {
      if (run.source_branch_name) return run.source_branch_name;
      if (run.source_branch_id) {
        const branch = this.branches.find(b => b.id == run.source_branch_id);
        return branch ? branch.name : null;
      }
      return null;
    },
    getRunNodeVersionName(run) {
      if (run.node_version_name) return run.node_version_name;
      if (run.node_version_id) {
        return this.getNodeVersion(run.node_version_id);
      }
      return null;
    },
  },
};
</script>
